import type { CalendarView, DeliverableStatus, GroupByOption } from "@dream/config/constants";
import { DateTime } from "luxon";

/**
 * Contagem de filtros ativos no calendário.
 *
 * Usada no selo do botão Filtros e nos testes: a barra não lista mais todos
 * os selects ao mesmo tempo, então o número precisa ser estável e explícito.
 * `colorBy` não entra — muda só a pintura, não o recorte dos dados.
 */
export function countActiveCalendarFilters(input: {
  search: string;
  statuses: DeliverableStatus[] | string[];
  clientIds: string[];
  projectIds: string[];
  typeIds: string[];
  assigneeIds: string[];
  groupBy: GroupByOption | string;
}): number {
  let count = 0;
  if (input.search.trim() !== "") count += 1;
  if (input.statuses.length > 0) count += 1;
  if (input.clientIds.length > 0) count += 1;
  if (input.projectIds.length > 0) count += 1;
  if (input.typeIds.length > 0) count += 1;
  if (input.assigneeIds.length > 0) count += 1;
  if (input.groupBy !== "none") count += 1;
  return count;
}

export type CalendarFilterDimension =
  | "status"
  | "client"
  | "project"
  | "type"
  | "assignee"
  | "groupBy";

export interface CalendarFilterChip {
  dimension: CalendarFilterDimension;
  label: string;
}

/**
 * Chips visíveis com o painel fechado.
 *
 * A busca fica de fora de propósito: já vive no campo da barra. Cada dimensão
 * vira um chip único, mesmo quando a URL carrega vários ids.
 */
export function listActiveCalendarFilterChips(input: {
  statuses: string[];
  statusLabel?: string | null;
  clientIds: string[];
  clientLabel?: string | null;
  projectIds: string[];
  projectLabel?: string | null;
  typeIds: string[];
  typeLabel?: string | null;
  assigneeIds: string[];
  assigneeLabel?: string | null;
  groupBy: string;
  groupByLabel?: string | null;
}): CalendarFilterChip[] {
  const chips: CalendarFilterChip[] = [];

  if (input.statuses.length > 0) {
    chips.push({
      dimension: "status",
      label: extraCount(input.statusLabel ?? "Status", input.statuses.length),
    });
  }
  if (input.clientIds.length > 0) {
    chips.push({
      dimension: "client",
      label: extraCount(input.clientLabel ?? "Cliente", input.clientIds.length),
    });
  }
  if (input.projectIds.length > 0) {
    chips.push({
      dimension: "project",
      label: extraCount(input.projectLabel ?? "Projeto", input.projectIds.length),
    });
  }
  if (input.typeIds.length > 0) {
    chips.push({
      dimension: "type",
      label: extraCount(input.typeLabel ?? "Tipo", input.typeIds.length),
    });
  }
  if (input.assigneeIds.length > 0) {
    chips.push({
      dimension: "assignee",
      label: extraCount(input.assigneeLabel ?? "Responsável", input.assigneeIds.length),
    });
  }
  if (input.groupBy !== "none") {
    chips.push({
      dimension: "groupBy",
      label: input.groupByLabel ?? "Agrupado",
    });
  }

  return chips;
}

function extraCount(label: string, count: number): string {
  return count > 1 ? `${label} +${count - 1}` : label;
}

/**
 * Clique na lista “Meus calendários”: um cliente por vez.
 *
 * Com a lista vazia (todos visíveis), o primeiro clique isola aquele cliente.
 * Clicar de novo no mesmo tira o recorte. Não acumula — o filtro avançado
 * da barra continua aceitando vários ids.
 */
export function nextExclusiveIds(current: string[], clicked: string): string[] {
  if (current.length === 1 && current[0] === clicked) return [];
  return [clicked];
}

/** Rótulo do recorte acima da grade (cliente, minhas tarefas, ou os dois). */
export function calendarSliceTitle(
  clientName: string | null,
  assigneeLabel: string | null,
): string {
  if (clientName !== null && assigneeLabel !== null) return `${clientName} · ${assigneeLabel}`;
  return clientName ?? assigneeLabel ?? "Recorte";
}

export function calendarPeriodPhrase(view: CalendarView): string {
  switch (view) {
    case "dia":
      return "neste dia";
    case "semana":
      return "nesta semana";
    case "linha":
      return "neste período";
    case "agenda":
      return "nesta agenda";
    default:
      return "neste mês";
  }
}

/** “15 de setembro” no atalho para a próxima entrega do recorte. */
export function formatSliceJumpLabel(date: Date, timezone: string): string {
  return DateTime.fromJSDate(date, { zone: timezone }).setLocale("pt-BR").toFormat("d 'de' LLLL");
}

function titleCaseFirst(value: string): string {
  if (value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Rótulo do período na barra.
 *
 * `compact` encurta o dia e a semana para caber no header móvel sem quebrar
 * a linha do calendário.
 */
export function formatPeriodLabel(
  view: CalendarView,
  focusDate: Date,
  timezone: string,
  compact = false,
  timelineDays = 28,
): string {
  const dt = DateTime.fromJSDate(focusDate, { zone: timezone }).setLocale("pt-BR");

  switch (view) {
    case "dia":
      return compact
        ? titleCaseFirst(dt.toFormat("ccc, dd LLL yyyy"))
        : titleCaseFirst(dt.toFormat("cccc, dd 'de' LLLL 'de' yyyy"));
    case "semana": {
      const start = dt.startOf("day").minus({ days: dt.weekday - 1 });
      const end = start.plus({ days: 6 });
      if (compact) {
        return start.month === end.month
          ? `${start.toFormat("dd")}–${end.toFormat("dd LLL")}`
          : `${start.toFormat("dd LLL")} – ${end.toFormat("dd LLL")}`;
      }
      return start.month === end.month
        ? `${start.toFormat("dd")}–${end.toFormat("dd 'de' LLLL yyyy")}`
        : `${start.toFormat("dd LLL")} – ${end.toFormat("dd LLL yyyy")}`;
    }
    case "linha":
      return compact
        ? `${dt.toFormat("dd LLL")} + ${timelineDays}d`
        : `${titleCaseFirst(dt.toFormat("dd 'de' LLLL"))} + ${timelineDays} dias`;
    default:
      return compact
        ? titleCaseFirst(dt.toFormat("LLL yyyy"))
        : titleCaseFirst(dt.toFormat("LLLL yyyy"));
  }
}
